import React, { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { Level, VISIBILITY_LEVEL } from '../types/level';

interface VisibilityLevelContext {
  // Level Management
  visibilityLevel: Level;
  updateVisibilityLevel: (value: Level) => void;
  isLevelEnabled: (level: Level) => boolean;
}

export const VisibilityLevelContext = createContext<VisibilityLevelContext>({
  visibilityLevel: VISIBILITY_LEVEL.BASIC,
  updateVisibilityLevel: () => {},
  isLevelEnabled: (level: Level) => level === VISIBILITY_LEVEL.BASIC,
});

export const VisibilityLevelContextProvider = ({ children }: PropsWithChildren) => {
  const [visibilityLevel, setVisibilityLevel] = useState<Level>(VISIBILITY_LEVEL.BASIC);

  const isLevelEnabled = useCallback(
    (level: Level) => {
      const levelIndex = levelsInRankedOrder.indexOf(level);
      if (levelIndex === -1) {
        return false;
      }
      return levelsInRankedOrder.indexOf(level) <= levelsInRankedOrder.indexOf(visibilityLevel);
    },
    [visibilityLevel]
  );

  return (
    <VisibilityLevelContext.Provider
      value={{ visibilityLevel, updateVisibilityLevel: setVisibilityLevel, isLevelEnabled }}
    >
      {children}
    </VisibilityLevelContext.Provider>
  );
};

export const useVisibilityLevelContext = () => {
  const context = useContext(VisibilityLevelContext);

  if (!context) {
    throw new Error('useVisibilityLevelContext must be used within a VisibilityLevelProvider');
  }

  return context;
};

const levelsInRankedOrder: Level[] = ['basic', 'advanced', 'expert'];
